import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, RefreshCw, RotateCcw } from 'lucide-react';

import { ApiError, request } from '@/api/client';
import type { Metric, RecomputeStatus } from '@/api/types';
import { PageHeader } from '@/components/AppShell';
import { DataTable, type Column } from '@/components/DataTable';
import { useToast } from '@/components/Toast';
import {
  Badge,
  Button,
  ErrorState,
  Field,
  Modal,
  TableSkeleton,
  TextInput,
} from '@/components/ui';
import { decimal } from '@/lib/format';
import { useResource } from '@/lib/hooks';

export function ScoringPage() {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const [activeOnly, setActiveOnly] = useState(true);
  const [editing, setEditing] = useState<Metric | null>(null);
  const [job, setJob] = useState<RecomputeStatus | null>(null);
  const [statusError, setStatusError] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmApply, setConfirmApply] = useState(false);
  const language = i18n.language;

  const { data, error, loading, reload } = useResource<Metric[]>(
    '/api/v1/admin/scoring/metrics',
    undefined,
    [],
  );

  const running = job?.status === 'queued' || job?.status === 'running' || job?.status === 'publishing';
  const refreshStatus = useCallback(async () => {
    try {
      const next = await request<RecomputeStatus>('/api/v1/admin/scoring/recompute');
      setJob(next);
      setStatusError(false);
      return next;
    } catch {
      setStatusError(true);
      return null;
    }
  }, []);
  const refreshAll = useCallback(() => {
    reload();
    void refreshStatus();
  }, [reload, refreshStatus]);

  useEffect(() => {
    void refreshStatus();
  }, [refreshStatus]);

  useEffect(() => {
    if (!running) return;
    let cancelled = false;
    let timer: number;
    async function poll() {
      const next = await refreshStatus();
      if (cancelled) return;
      if (next?.status === 'done' || next?.status === 'failed') reload();
      if (!next || next.status === 'queued' || next.status === 'running' || next.status === 'publishing') {
        timer = window.setTimeout(() => { void poll(); }, 2000);
      }
    }
    timer = window.setTimeout(() => { void poll(); }, 2000);
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [running, refreshStatus, reload]);

  async function startRecompute() {
    if (!job || job.pending_count === 0 || running || busy) return;
    setBusy(true);
    try {
      const next = await request<RecomputeStatus>('/api/v1/admin/scoring/recompute', {
        method: 'POST',
        body: { draft_version: job.draft_version },
      });
      setJob(next);
      if (next.status === 'done') reload();
      setConfirmApply(false);
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 409) setConfirmApply(false);
      handleError(caught);
    } finally {
      setBusy(false);
    }
  }

  function handleError(caught: unknown) {
    if (caught instanceof ApiError && caught.status === 409) {
      toast.failure(t('scoring.conflict'));
      refreshAll();
    } else {
      toast.failure(t('common.loadFailed'));
    }
  }

  async function reset(metric: Metric) {
    if (!job || running || busy) return;
    setBusy(true);
    try {
      await request(`/api/v1/admin/scoring/metrics/${metric.code}/reset`, {
        method: 'POST',
        query: { expected_draft_version: job.draft_version },
      });
      toast.success(t('scoring.resetDone'));
      refreshAll();
    } catch (caught) {
      handleError(caught);
    } finally {
      setBusy(false);
    }
  }

  async function discard() {
    if (!job || running || busy) return;
    setBusy(true);
    try {
      await request<RecomputeStatus>('/api/v1/admin/scoring/drafts', {
        method: 'DELETE',
        query: { expected_draft_version: job.draft_version },
      });
      toast.success(t('scoring.discarded'));
      refreshAll();
    } catch (caught) {
      handleError(caught);
    } finally {
      setBusy(false);
    }
  }

  const metrics = (data ?? []).filter((metric) => !activeOnly || metric.is_active);
  const pending = job?.pending_count ?? (data ?? []).filter((metric) => metric.has_pending_change).length;

  const columns: Column<Metric>[] = [
    {
      key: 'metric',
      header: t('scoring.metric'),
      render: (metric) => (
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="truncate font-medium text-ink">{metric.skill_name}</span>
            {metric.has_pending_change ? (
              <Badge tone="caution">{t('scoring.pending')}</Badge>
            ) : metric.is_overridden ? (
              <Badge tone="info">{t('scoring.changed')}</Badge>
            ) : null}
            {!metric.is_active ? (
              <Badge tone="neutral" title={t('scoring.parkedHint')}>
                {t('scoring.parked')}
              </Badge>
            ) : null}
          </div>
          <div className="truncate text-2xs text-ink-subtle">
            {metric.category} · {t(`scoring.logic.${metric.logic}`)}
          </div>
        </div>
      ),
    },
    {
      key: 'unit',
      header: t('scoring.unit'),
      render: (metric) => <span className="text-ink-muted">{metric.unit}</span>,
    },
    {
      key: 'baseline',
      header: t('scoring.baselineFull'),
      align: 'right',
      render: (metric) => (
        <ValueCell
          current={metric.baseline_100}
          draft={metric.draft_baseline_100}
          shipped={metric.default_baseline_100}
          language={language}
          currentLabel={t('scoring.activeValue')}
          shippedLabel={t('scoring.shipped')}
          draftLabel={t('scoring.draft')}
        />
      ),
    },
    {
      key: 'elite',
      header: t('scoring.eliteFull'),
      align: 'right',
      render: (metric) => (
        <ValueCell
          current={metric.elite_1000}
          draft={metric.draft_elite_1000}
          shipped={metric.default_elite_1000}
          language={language}
          currentLabel={t('scoring.activeValue')}
          shippedLabel={t('scoring.shipped')}
          draftLabel={t('scoring.draft')}
        />
      ),
    },
    {
      key: 'curve',
      header: t('scoring.curve'),
      align: 'right',
      render: (metric) => (
        <ValueCell
          current={metric.curve_factor}
          draft={metric.draft_curve_factor}
          shipped={metric.default_curve_factor}
          language={language}
          currentLabel={t('scoring.activeValue')}
          shippedLabel={t('scoring.shipped')}
          draftLabel={t('scoring.draft')}
        />
      ),
    },
    {
      key: 'actions',
      header: t('common.edit'),
      align: 'right',
      render: (metric) => (
        <div className="flex justify-end gap-1">
          <Button className="min-h-11 text-xs" disabled={running || busy || !job || statusError} onClick={() => setEditing(metric)}>
            {t('scoring.edit')}
          </Button>
          {(metric.is_overridden || metric.has_pending_change) ? (
            <Button
              className="min-h-11 min-w-11 text-xs"
              icon={<RotateCcw className="h-3.5 w-3.5" />}
              onClick={() => reset(metric)}
              disabled={running || busy || !job || statusError}
              aria-label={t('scoring.resetMetric')}
              title={t('scoring.resetMetric')}
            />
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader title={t('scoring.title')} subtitle={t('scoring.subtitle')} />

      <section className="card mb-4 p-4">
        <h2 className="text-sm font-semibold text-ink">{t('scoring.recomputeTitle')}</h2>
        <p className="mt-1 max-w-3xl text-sm text-ink-muted">
          {t('scoring.recomputeBody')}
        </p>

        {pending > 0 ? (
          <p className="mt-2 flex items-start gap-1.5 text-sm text-amber-800 dark:text-amber-200">
            <AlertTriangle aria-hidden className="mt-0.5 h-4 w-4 shrink-0" />
            {t('scoring.pendingChanges', { count: pending })}
          </p>
        ) : null}

        <div className="mt-3 flex flex-wrap items-center gap-3">
          <Button
            variant="primary"
            icon={<RefreshCw className="h-4 w-4" />}
            disabled={!job || statusError || pending === 0 || running || busy || loading || !!error}
            onClick={() => setConfirmApply(true)}
          >
            {job?.status === 'failed' ? t('scoring.retryApply') : t('scoring.applyChanges')}
          </Button>

          {pending > 0 ? (
            <Button disabled={running || busy || !job || statusError} onClick={discard}>
              {t('scoring.discardChanges')}
            </Button>
          ) : null}

          <p aria-live="polite" className="text-xs text-ink-subtle">
            {statusError ? (
              <><span>{t('scoring.statusFailed')}</span>{' '}<Button onClick={refreshAll}>{t('common.retry')}</Button></>
            ) : job?.status === 'queued'
              ? t('scoring.recomputeQueued')
              : job?.status === 'running'
              ? t('scoring.recomputeRunning', {
                  processed: job.processed,
                  total: job.total,
                })
              : job?.status === 'publishing'
                ? t('scoring.recomputePublishing')
              : job?.status === 'failed' && pending > 0
                ? t('scoring.recomputeFailed')
              : job && pending > 0
                ? t('scoring.notApplied')
              : job?.status === 'done'
                ? t('scoring.recomputeDone', {
                    processed: job.processed,
                    pillars: job.pillars_touched,
                  })
                : job?.status === 'failed'
                  ? t('scoring.lastApplyFailed')
                  : job ? t('scoring.recomputeIdle') : t('scoring.statusLoading')}
          </p>
        </div>

        {job?.status === 'done' && pending === 0 && job.orphaned_record_count > 0 ? (
          <p className="mt-2 text-xs text-ink-muted">
            {t('scoring.orphanedRecords', { count: job.orphaned_record_count })}
          </p>
        ) : null}

        {running && job.total > 0 ? (
          <div
            className="mt-3 h-1.5 overflow-hidden rounded-full bg-surface-sunken"
            role="progressbar"
            // A progressbar with no name announces as "progress bar, 40%" and
            // nothing else; the reader has no idea what is progressing.
            aria-label={t('scoring.recomputeTitle')}
            aria-valuemin={0}
            aria-valuemax={job.total}
            aria-valuenow={job.processed}
          >
            <div
              className="h-full rounded-full bg-brand transition-[width]"
              style={{ width: `${Math.round((job.processed / job.total) * 100)}%` }}
            />
          </div>
        ) : null}
      </section>

      <div className="card">
        <div className="flex items-center justify-between gap-3 border-b border-line p-3">
          <label className="flex min-h-[2.25rem] items-center gap-2 text-sm text-ink">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-line text-brand"
              checked={activeOnly}
              onChange={(event) => setActiveOnly(event.target.checked)}
            />
            {t('scoring.activeOnly')}
          </label>
          <span className="text-xs text-ink-subtle tabular">{metrics.length}</span>
        </div>

        {error ? (
          <ErrorState message={t('common.loadFailed')} onRetry={reload} />
        ) : loading && !data ? (
          <TableSkeleton columns={6} />
        ) : (
          <>
            <div className="divide-y divide-line xl:hidden">
              {metrics.map((metric) => (
                <article key={metric.code} className="p-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      {columns[0].render(metric)}
                      <div className="mt-1 text-xs text-ink-subtle">
                        {columns[1].header}: {columns[1].render(metric)}
                      </div>
                    </div>
                    {columns[5].render(metric)}
                  </div>
                  <dl className="mt-3 grid grid-cols-2 gap-3 border-t border-line pt-3 sm:grid-cols-3">
                    {columns.slice(2, 5).map((column) => (
                      <div key={column.key} className="min-w-0">
                        <dt className="mb-1 text-2xs font-medium text-ink-subtle">{column.header}</dt>
                        <dd className="text-sm tabular">{column.render(metric)}</dd>
                      </div>
                    ))}
                  </dl>
                </article>
              ))}
            </div>
            <div className="hidden xl:block">
              <DataTable
                caption={t('scoring.title')}
                columns={columns}
                rows={metrics}
                rowKey={(metric) => metric.code}
              />
            </div>
          </>
        )}
      </div>

      {editing ? (
        <EditMetric
          metric={editing}
          draftVersion={job?.draft_version ?? editing.draft_version}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            toast.success(t('scoring.saved'));
            refreshAll();
          }}
          onConflict={refreshAll}
        />
      ) : null}

      {confirmApply ? (
        <Modal
          open
          onClose={() => { if (!busy) setConfirmApply(false); }}
          title={t('scoring.confirmTitle')}
          footer={<>
            <Button disabled={busy} onClick={() => setConfirmApply(false)}>{t('common.cancel')}</Button>
            <Button
              variant="primary"
              loading={busy}
              disabled={!job || statusError || pending === 0 || running || loading || !!error}
              onClick={startRecompute}
            >
              {t('scoring.applyChanges')}
            </Button>
          </>}
        >
          <p className="text-sm text-ink">{t('scoring.confirmBody', { count: pending })}</p>
        </Modal>
      ) : null}
    </>
  );
}

function ValueCell({
  current,
  draft,
  shipped,
  language,
  currentLabel,
  shippedLabel,
  draftLabel,
}: {
  current: number;
  draft: number | null;
  shipped: number;
  language: string;
  currentLabel: string;
  shippedLabel: string;
  draftLabel: string;
}) {
  const changed = current !== shipped;
  return (
    <div>
      <div className="font-medium text-ink">{decimal(current, language)}</div>
      {draft !== null && draft !== current ? (
        <div className="text-2xs text-ink-subtle">{currentLabel}</div>
      ) : null}
      {draft !== null && draft !== current ? (
        <div className="text-2xs font-medium text-amber-800 dark:text-amber-200">
          {draftLabel}: {decimal(draft, language)}
        </div>
      ) : null}
      {/* The shipped value stays visible next to the tuned one, so the size of
          a change is readable without opening the dialog. */}
      {changed ? (
        <div className="text-2xs text-ink-subtle">
          {shippedLabel}: {decimal(shipped, language)}
        </div>
      ) : null}
    </div>
  );
}

function EditMetric({
  metric,
  draftVersion,
  onClose,
  onSaved,
  onConflict,
}: {
  metric: Metric;
  draftVersion: number;
  onClose: () => void;
  onSaved: () => void;
  onConflict: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const [baseline, setBaseline] = useState(String(metric.draft_baseline_100 ?? metric.baseline_100));
  const [elite, setElite] = useState(String(metric.draft_elite_1000 ?? metric.elite_1000));
  const [curve, setCurve] = useState(String(metric.draft_curve_factor ?? metric.curve_factor));
  const [busy, setBusy] = useState(false);

  const baselineValue = Number(baseline);
  const eliteValue = Number(elite);
  const curveValue = Number(curve);

  const baselineNumeric = baseline.trim() !== '' && Number.isFinite(baselineValue);
  const eliteNumeric = elite.trim() !== '' && Number.isFinite(eliteValue);
  const curveNumeric = curve.trim() !== '' && Number.isFinite(curveValue);
  const baselineError = !baselineNumeric
    ? t('scoring.numberRequired')
    : baselineValue < 0 || (baselineValue === 0 && metric.default_baseline_100 !== 0)
      ? t(metric.default_baseline_100 === 0 ? 'scoring.baselineRange' : 'scoring.baselinePositive')
      : undefined;
  const directionOk = metric.logic === 'lower_is_better'
    ? eliteValue < baselineValue
    : eliteValue > baselineValue;
  const eliteError = !eliteNumeric
    ? t('scoring.numberRequired')
    : eliteValue <= 0
      ? t('scoring.eliteRange')
      : !baselineError && !directionOk
        ? t(metric.logic === 'lower_is_better' ? 'scoring.directionLower' : 'scoring.directionHigher')
        : undefined;
  const curveError = !curveNumeric
    ? t('scoring.numberRequired')
    : curveValue < 0.1 || curveValue > 10
      ? t('errors.curveRange')
      : undefined;
  const valuesOk = !baselineError && !eliteError && !curveError;

  async function save() {
    if (!valuesOk || busy) return;
    setBusy(true);
    try {
      await request(`/api/v1/admin/scoring/metrics/${metric.code}`, {
        method: 'PUT',
        body: {
          baseline_100: baselineValue,
          elite_1000: eliteValue,
          curve_factor: curveValue,
          expected_draft_version: draftVersion,
        },
      });
      onSaved();
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 409) {
        toast.failure(t('scoring.conflict'));
        onConflict();
        onClose();
      } else {
        toast.failure(t('scoring.saveFailed'));
      }
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      onClose={() => { if (!busy) onClose(); }}
      title={t('scoring.editTitle', { name: metric.skill_name })}
      description={`${metric.category} · ${t(`scoring.logic.${metric.logic}`)} · ${metric.unit}`}
      footer={
        <>
          <Button disabled={busy} onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            loading={busy}
            disabled={!valuesOk}
            onClick={save}
          >
            {busy ? t('common.saving') : t('scoring.saveDraft')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field
          label={t('scoring.baselineFull')}
          hint={`${t('scoring.shipped')}: ${metric.default_baseline_100}`}
          error={baselineError}
        >
          {(id, describedBy) => (
            <TextInput
              id={id}
              aria-describedby={describedBy}
              type="number"
              inputMode="decimal"
              step="any"
              min={0}
              value={baseline}
              invalid={!!baselineError}
              onChange={(event) => setBaseline(event.target.value)}
            />
          )}
        </Field>

        <Field
          label={t('scoring.eliteFull')}
          hint={`${t('scoring.shipped')}: ${metric.default_elite_1000}`}
          error={eliteError}
        >
          {(id, describedBy) => (
            <TextInput
              id={id}
              aria-describedby={describedBy}
              type="number"
              inputMode="decimal"
              step="any"
              min={0}
              value={elite}
              invalid={!!eliteError}
              onChange={(event) => setElite(event.target.value)}
            />
          )}
        </Field>

        <Field
          label={t('scoring.curve')}
          hint={`${t('scoring.shipped')}: ${metric.default_curve_factor}`}
          error={curveError}
        >
          {(id, describedBy) => (
            <TextInput
              id={id}
              aria-describedby={describedBy}
              type="number"
              inputMode="decimal"
              step="0.1"
              min={0.1}
              max={10}
              value={curve}
              invalid={!!curveError}
              onChange={(event) => setCurve(event.target.value)}
            />
          )}
        </Field>

        <p className="rounded-lg bg-surface-sunken px-3 py-2 text-xs text-ink-muted">
          {t('scoring.saveHint')}
        </p>
      </div>
    </Modal>
  );
}
